import { Box, Button, Heading, Text } from "@vinomofo/components";

import { COLORS } from "../../../styles";
import styled from "styled-components";
import { trackEvent } from "../../../utils/track-event";
import { useLocation } from "@reach/router";

const SectionHeaderWrapper = styled(Box)<{ type?: string }>`
  * + * {
    margin-top: ${({ type }) => (type === "whiskymofo" ? "-6px" : "12px")};
  }
`;
const CTAButton = styled(Button)`
  border-width: 1px;
  border-style: solid;

  :hover {
    background-color: transparent;
    border-color: ${COLORS.mofoGold};
  }
`;

export interface SectionHeaderProps {
  heading?: string;
  subheading?: string;
  align?: "left" | "center" | "right";
  type?: "regular" | "large" | "whiskymofo";
  ctaLabel?: string;
  ctaLink?: string;
}

export const SectionHeader = ({
  heading,
  subheading,
  align = "center",
  type,
  ctaLabel,
  ctaLink,
}: SectionHeaderProps): JSX.Element => {
  const location = useLocation();

  let renderView = (
    <SectionHeaderWrapper width="100%" textAlign={align} py={4} type={type}>
      {subheading && (
        <Heading variant="xxs" as="p" fontWeight="500" color="brand">
          {subheading}
        </Heading>
      )}
      {heading && (
        <Heading variant="md" as="h3">
          {heading}
        </Heading>
      )}
    </SectionHeaderWrapper>
  );
  if (type === "large")
    renderView = (
      <SectionHeaderWrapper
        width="100%"
        textAlign={align}
        py={4}
        mb={20}
        type={type}
      >
        {subheading && (
          <Box
            borderBottom="2px solid"
            borderBottomColor="mofoGreen"
            width="fit-content"
            margin="0 auto"
          >
            <Heading
              variant="xxl"
              fontSize={84}
              as="p"
              fontWeight="600"
              color="black"
            >
              {subheading}
            </Heading>
          </Box>
        )}
        {heading && (
          <Heading variant="sm" as="p" fontSize={20} fontStyle={"italic"}>
            {heading}
          </Heading>
        )}
      </SectionHeaderWrapper>
    );
  if (type === "whiskymofo")
    renderView = (
      <SectionHeaderWrapper
        width="100%"
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        py={4}
        gridColumnGap="15px"
        flexDirection={ctaLink && ctaLabel ? ["row"] : ["column", "row", "row"]}
        type={type}
      >
        {heading && (
          <Box
            width={
              ctaLink && ctaLabel
                ? ["fit-content"]
                : ["100%", "fit-content", "fit-content"]
            }
          >
            <Heading
              variant="lg"
              as="p"
              fontSize={[40, 60, 70]}
              color={COLORS.white}
              fontFamily={"specialHeading"}
            >
              {heading}
            </Heading>
          </Box>
        )}
        {subheading && (
          <Box
            width={
              ctaLink && ctaLabel
                ? ["50%", "50%", "50%"]
                : ["100%", "70%", "50%"]
            }
            display={
              ctaLink && ctaLabel
                ? ["none", "unset", "unset"]
                : ["unset", "unset", "unset"]
            }
          >
            <Text
              fontSize={"14px"}
              textAlign={
                ctaLink && ctaLabel ? ["left"] : ["justify", "right", "right"]
              }
              as="p"
              fontWeight="100"
              color={COLORS.white}
            >
              {subheading}
            </Text>
          </Box>
        )}
        {ctaLink && ctaLabel && (
          <Box width={"50%"} textAlign={"right"}>
            <CTAButton
              variant="outline"
              width={126}
              backgroundColor={"transparent"}
              color={COLORS.white}
              onClick={() => {
                trackEvent("Section Header CTA Clicked", {
                  cta_label: ctaLabel,
                  cta_link: ctaLink,
                  section_heading: heading,
                  location: location?.pathname,
                });
                window.open(ctaLink, "_blank");
              }}
              borderColor={COLORS.mofoGold}
              fontFamily="heading"
              fontWeight="400"
              hoverColor={COLORS.mofoGold}
            >
              {ctaLabel}
            </CTAButton>
          </Box>
        )}
      </SectionHeaderWrapper>
    );

  return renderView;
};
